"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.toZonedDateTimeWithCustomFormat = exports.toRawZonedDateTime = exports.toZonedDateTime = exports.ZonedDateTime = exports.ZonedDateTimeConfig = exports.hasTimezoneInDateTimeString = exports.regExTypeDateTimeWithTZ = void 0;
const tslib_1 = require("tslib");
const moment_timezone_1 = tslib_1.__importDefault(require("moment-timezone"));
const local_date_1 = require("./local-date");
const local_date_time_1 = require("./local-date-time");
const utils_1 = require("./utils");
exports.regExTypeDateTimeWithTZ = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.(\d{3,6}))?([+-]\d{2}:{0,1}\d{2}|Z){0,1}\[(.+)\]$/;
const regExTimezone = /\[.+\]$/;
function hasTimezoneInDateTimeString(date) {
    return date.includes('[') || /(?:Z|[+-]\d{2}:?\d{2})$/.test(date);
}
exports.hasTimezoneInDateTimeString = hasTimezoneInDateTimeString;
function extractDateAndTimezoneFromString(date) {
    if (date.includes('[') && exports.regExTypeDateTimeWithTZ.test(date)) {
        const tzMatch = new RegExp(exports.regExTypeDateTimeWithTZ).exec(date);
        if (tzMatch) {
            return {
                date: date.replace(regExTimezone, ''),
                tz: tzMatch[9]
            };
        }
    }
    return undefined;
}
exports.ZonedDateTimeConfig = {
    toStringFormatWithoutTimezone: undefined
};
/**
 * A ZonedDateTime represents a date-time with or without a time zone in the ISO-8601 calendar system.
 *
 * @example toZonedDateTime('2020-12-30T13:00:00.000Z')
 * @example toZonedDateTime(new Date('2020-12-30T13:00:00.000Z'))
 * @example ZonedDateTime.now(TIMEZONE)
 */
class ZonedDateTime extends local_date_time_1.LocalDateTime {
    get options() {
        return {
            format: this._format,
            locale: this._locale,
            timezone: this._timezone
        };
    }
    get moment() {
        return (0, utils_1.getMoment)(this.__originalDate__, this.options);
    }
    get timezone() {
        return this._timezone;
    }
    createNew(date, options) {
        return new ZonedDateTime(local_date_1.constructSymbol, date, Object.assign(Object.assign(Object.assign({}, this.options), options), { timezone: (options === null || options === void 0 ? void 0 : options.timezone) || this.options.timezone }));
    }
    constructor(_symbol, date, options) {
        super(_symbol, date, options);
        Object.defineProperty(this, "_timezone", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this._type_ = 'datetime';
        let timezone = options === null || options === void 0 ? void 0 : options.timezone;
        const dateWithTZ = typeof date === 'string'
            ? extractDateAndTimezoneFromString(date)
            : undefined;
        if (dateWithTZ) {
            if (timezone && timezone !== dateWithTZ.tz) {
                console.warn('[ZonedDateTime]: Provided two different timezones, both in date-string and via parameter. Proceeding using the one provided via date-string.');
            }
            this.__originalDate__ = dateWithTZ.date;
            timezone = dateWithTZ.tz;
        }
        this._timezone = timezone;
    }
    static now(timezone) {
        const now = (0, moment_timezone_1.default)();
        return toZonedDateTime(timezone ? now.tz(timezone) : now, timezone || '');
    }
    static from(year, month, day, hour, minutes, seconds, ms) {
        return ZonedDateTime.now('')
            .setYear(year)
            .setMonth(month !== null && month !== void 0 ? month : 0)
            .setDate(day !== null && day !== void 0 ? day : 1)
            .setHour(hour !== null && hour !== void 0 ? hour : 0)
            .setMinute(minutes !== null && minutes !== void 0 ? minutes : 0)
            .setSecond(seconds !== null && seconds !== void 0 ? seconds : 0)
            .setMillisecond(ms !== null && ms !== void 0 ? ms : 0);
    }
    /**
     * Gets the clients timezone
     */
    static getLocalTZ() {
        return moment_timezone_1.default.tz.guess();
    }
    /**
     * Reset the timezone
     */
    setTZ(timezone) {
        return this.createNew(this.moment, Object.assign(Object.assign({}, this.options), { timezone: timezone || undefined }));
    }
    /**
     * Resets the timezone to the clients' local timezone
     */
    setLocalTZ() {
        return this.setTZ(ZonedDateTime.getLocalTZ());
    }
    getTimezoneOffset() {
        return this.toDate().getTimezoneOffset();
    }
    /**
     * If no timezone was provided, fall back to the clients timezone
     */
    isToday() {
        return this.isSameDay(ZonedDateTime.now(this.timezone || ZonedDateTime.getLocalTZ()));
    }
    /**
     * If no timezone was provided, fall back to the clients timezone
     */
    isTomorrow() {
        return (this.diff(ZonedDateTime.now(this.timezone || ZonedDateTime.getLocalTZ()).startOf('day'), 'day') === 1);
    }
    /**
     * If no timezone was provided, fall back to the clients timezone
     */
    isYesterday() {
        return (this.diff(ZonedDateTime.now(this.timezone || ZonedDateTime.getLocalTZ()).endOf('day'), 'day') === -1);
    }
    /**
     * If no timezone was provided, fall back to the clients timezone
     */
    isFuture() {
        return (this.getTime() >
            ZonedDateTime.now(this.timezone || ZonedDateTime.getLocalTZ()).getTime());
    }
    /**
     * If no timezone was provided, fall back to the clients timezone
     */
    isThisWeek() {
        return super.isThisWeek(this.timezone || ZonedDateTime.getLocalTZ());
    }
    /**
     * If no timezone was provided, fall back to the clients timezone
     */
    isNextWeek() {
        return super.isNextWeek(this.timezone || ZonedDateTime.getLocalTZ());
    }
    /**
     * If no timezone was provided, fall back to the clients timezone
     */
    isLastWeek() {
        return super.isLastWeek(this.timezone || ZonedDateTime.getLocalTZ());
    }
    /**
     * If no timezone was provided, fall back to the clients timezone
     */
    isThisMonth() {
        return super.isThisMonth(this.timezone || ZonedDateTime.getLocalTZ());
    }
    /**
     * If no timezone was provided, fall back to the clients timezone
     */
    isNextMonth() {
        return super.isNextMonth(this.timezone || ZonedDateTime.getLocalTZ());
    }
    /**
     * If no timezone was provided, fall back to the clients timezone
     */
    isLastMonth() {
        return super.isLastMonth(this.timezone || ZonedDateTime.getLocalTZ());
    }
    formatForServer() {
        const { _timezone } = this;
        if (!_timezone &&
            exports.ZonedDateTimeConfig.toStringFormatWithoutTimezone === 'iso') {
            return this.moment.toISOString();
        }
        const parts = [super.formatForServer()];
        if (_timezone) {
            parts.push(this.format('Z'), `[${_timezone}]`);
        }
        return parts.join('');
    }
}
exports.ZonedDateTime = ZonedDateTime;
function toZonedDateTime(date, appendTimezone) {
    if (!date) {
        return undefined;
    }
    const dateWithTZ = typeof date === 'string'
        ? extractDateAndTimezoneFromString(date)
        : undefined;
    const hasTimezoneInStr = Boolean(dateWithTZ === null || dateWithTZ === void 0 ? void 0 : dateWithTZ.tz);
    if (appendTimezone && !hasTimezoneInStr) {
        if (typeof date === 'string' && date.endsWith('Z')) {
            return toRawZonedDateTime(date).setTZ(appendTimezone);
        }
        const rawDate = toRawZonedDateTime(date);
        const offset = rawDate.toMoment().tz(appendTimezone).format('Z');
        if (rawDate.isValid()) {
            const toStringFormat = exports.ZonedDateTimeConfig.toStringFormatWithoutTimezone;
            if (toStringFormat) {
                exports.ZonedDateTimeConfig.toStringFormatWithoutTimezone = undefined;
            }
            const newDate = toRawZonedDateTime(`${rawDate.toString()}${offset}[${appendTimezone}]`);
            if (toStringFormat) {
                exports.ZonedDateTimeConfig.toStringFormatWithoutTimezone =
                    toStringFormat;
            }
            return newDate;
        }
        throw new Error(`[timezone] The provided date (${date}) is not valid. If you're passing a custom-format please use toZonedDateTimeWithCustomFormat`);
    }
    return new ZonedDateTime(local_date_1.constructSymbol, date);
}
exports.toZonedDateTime = toZonedDateTime;
/**
 * Converts a date (DateTimeVariant) to ZonedDateTime without applying the timezone
 */
function toRawZonedDateTime(date) {
    return toZonedDateTime(date, '');
}
exports.toRawZonedDateTime = toRawZonedDateTime;
/**
 * Converts a date-string to ZonedDateTime with a custom format
 */
function toZonedDateTimeWithCustomFormat(date, format) {
    return toRawZonedDateTime(date).setFormat(format);
}
exports.toZonedDateTimeWithCustomFormat = toZonedDateTimeWithCustomFormat;
